import { COMMANDS, MEDIA_TYPE, DISPLAY_NAME } from '@/Utils/vice-constants';
import { SmartGlassesClient } from '@/Services/SocketioClient';
import useSessionStore from '@/Stores/useSessionStore';
import useIOTDevicesStore from '@/Stores/useIOTDevicesStore';
import LogService from '@/Services/LogService';

function useConsultationHandlers({
  onConferenceJoined,
  remoteUserProps,
  videoAudioRefs,
  resetMicState,
  myJitsiId,
  localConnectionQuality,
}) {
  const sessionStore = useSessionStore();
  const streamingDeviceStore = useIOTDevicesStore();

  const updateActiveCallAVState = (userId, trackType, trackState, jitsiUserId) => {
    sessionStore.addActiveCallParticipants({
      [userId]: {
        [trackType]: trackState,
        isRemoteUser: true,
        jitsiUserId,
      },
    });
  };

  const toggleHand = (state, userId, time) => {
    sessionStore.setHandRaised({
      [userId]: {
        state,
        time,
      },
    });
  };

  const onRemoteTrack = (track) => {
    track.on(JitsiMeetJS.events.track.TRACK_MUTE_CHANGED, (newTrack) => {
      // Update the UI and microphone when the audio or video track changes for a remote user
      const senderId = newTrack.getParticipantId();
      const mediaType = newTrack.getType();
      const isMuted = newTrack.isMuted();

      if (remoteUserProps[senderId]) {
        remoteUserProps[senderId][mediaType] = !isMuted;
      }

      const participantUserId = remoteUserProps[senderId].userId;
      updateActiveCallAVState(participantUserId, mediaType, !isMuted, senderId);
    });

    const participant = track.getParticipantId();
    if (remoteUserProps[participant]) {
      const mediaType = track.getType();
      const isMuted = track.isMuted();
      remoteUserProps[participant][mediaType] = !isMuted;

      if (!videoAudioRefs[participant]) return;
      if (track?.getType() === MEDIA_TYPE.VIDEO) {
        const videoEl = videoAudioRefs[participant].find((el) => el.nodeName === 'VIDEO');
        track.attach(videoEl);
        remoteUserProps[participant].videoType = track.videoType;
      }
      if (track?.getType() === MEDIA_TYPE.AUDIO) {
        const audioEl = videoAudioRefs[participant].find((el) => el.nodeName === 'AUDIO');
        track.attach(audioEl);
      }
      remoteUserProps[participant].tracks.push(track);

      // Update the visible screenshare flag
      if (/ScreenshareUser/.test(remoteUserProps[participant].name)) {
        remoteUserProps[participant].screenshareOff = false;
      }

      // setting the updated video and audio state for the participants
      const participantUserId = remoteUserProps[participant].userId;
      updateActiveCallAVState(participantUserId, mediaType, !isMuted, participant);
    }
  };

  const onLocalTrack = async (track) => {
    await LogService.logLocalTrack(track, 'ADDED');

    track.on(JitsiMeetJS.events.track.LOCAL_TRACK_STOPPED, async () => {
      await LogService.logLocalTrack(track, 'STOPPED');
    });

    track.on(JitsiMeetJS.events.track.TRACK_REMOVED, async () => {
      await LogService.logLocalTrack(track, 'REMOVED');
    });
  };

  const onTrackAdded = (track) => {
    if (!track.isLocal()) {
      onRemoteTrack(track);
    } else {
      onLocalTrack(track);
    }
  };

  // This handler will be used to remove the screenshare user when screenshare is stopped
  // we are only interested in videotrack of type 'desktop'
  const onTrackRemoved = (removedTrack) => {
    // remove event listeners on track
    removedTrack.removeEventListener(JitsiMeetJS.events.track.TRACK_MUTE_CHANGED, () => {});

    const participantId = removedTrack.getParticipantId();
    if (/ScreenshareUser/.test(remoteUserProps[participantId]?.name)) {
      removedTrack.detach(videoAudioRefs[participantId][1]);
      remoteUserProps[participantId].screenshareOff = true;
    }
  };

  /**
   * We set the only user to be added to the room to the vuzix device so that it the only stream
   * that is visible on the Pariticipant's screen during consultation
   */
  const onUserJoined = async (id, user) => {
    const name = user?.getDisplayName();
    const lastDashIndex = name?.lastIndexOf('-');
    const userId = name?.substr(lastDashIndex + 1);

    if (name !== DISPLAY_NAME.NAVIGATION_USER && name !== DISPLAY_NAME.OBSERVER) {
      // If the incoming user is vuzix, check whether one exists and
      // completely remove it and kick it from the stream
      // so that only the new one will be added
      if (name === DISPLAY_NAME.VUZIX) {
        for (const [key, value] of Object.entries(remoteUserProps)) {
          if (value.name === DISPLAY_NAME.VUZIX) {
            onUserLeft(key);
          }
        }
      }

      if (!remoteUserProps[id]) {
        remoteUserProps[id] = {};
      }
      remoteUserProps[id].name = name;
      remoteUserProps[id].role = user?.getRole();
      remoteUserProps[id].jitsiUserId = id;
      remoteUserProps[id].userId = userId;
      remoteUserProps[id].tracks = [];
      remoteUserProps[id][MEDIA_TYPE.AUDIO] = false;
      remoteUserProps[id][MEDIA_TYPE.VIDEO] = false;
      remoteUserProps[id].videoType = '';
      remoteUserProps[id].streamMuted ??= false;

      // flag to toggle the screenshare on and off;
      if (/ScreenshareUser/.test(name)) {
        remoteUserProps[id].screenshareOff = true;
      }

      // Initiate smartglass socket client after remoteUserProps instance has been added.
      if (name === DISPLAY_NAME.VUZIX) {
        streamingDeviceStore.iotSockets.smartglasses = await SmartGlassesClient(
          sessionStore.sessionId,
        );
      }

      // Add the remoteUserProperties in session store.
      updateActiveCallAVState(userId, MEDIA_TYPE.AUDIO, false, id);
      updateActiveCallAVState(userId, MEDIA_TYPE.VIDEO, false, id);
    }
  };

  const onUserLeft = (id) => {
    if (!remoteUserProps[id]) {
      return;
    }

    if (remoteUserProps[id]?.tracks) {
      for (const track of remoteUserProps[id].tracks) {
        if (track.getType() === MEDIA_TYPE.VIDEO) {
          const videoEl = videoAudioRefs[id].find((el) => el.nodeName === 'VIDEO');
          if (videoEl) track.detach(videoEl);
        }
        if (track.getType() === MEDIA_TYPE.AUDIO) {
          const audioEl = videoAudioRefs[id].find((el) => el.nodeName === 'AUDIO');
          if (audioEl) track.detach(audioEl);
        }
      }
    }
    delete remoteUserProps[id];
  };

  const onParticipantPropertyChanged = (user, propertyKey, oldValue, newValue) => {
    if (user !== undefined && propertyKey === 'avatar') {
      remoteUserProps[user.getId()].avatar = newValue;
    }
  };

  const onLocalStatsUpdated = (stats) => {
    localConnectionQuality.value = stats.connectionQuality;
    // console.info('🟢 CONS_LOC: ', stats.connectionQuality);
    // localConnectionQuality.value = Math.floor(Math.random() * 100);
  };

  const onRemoteStatsUpdated = (id, stats) => {
    if (remoteUserProps[id]) {
      remoteUserProps[id].connectionQuality = stats.connectionQuality;
      // console.info(`🟡 CONS_REM: (${remoteUserProps[id].name})`, stats.connectionQuality);
      // remoteUserProps[id].connectionQuality = Math.floor(Math.random() * 100);
    }
  };

  const events = {
    CONFERENCE: {
      TRACK_ADDED: onTrackAdded,
      TRACK_REMOVED: onTrackRemoved,
      CONFERENCE_JOINED: onConferenceJoined,
      USER_JOINED: onUserJoined,
      USER_LEFT: onUserLeft,
      PARTICIPANT_PROPERTY_CHANGED: onParticipantPropertyChanged,
    },
    CONN_QUALITY: {
      REMOTE_STATS_UPDATED: onRemoteStatsUpdated,
      LOCAL_STATS_UPDATED: onLocalStatsUpdated,
    },
  };

  // This command listener was added because TRACK_REMOVED AND TRACK_ADDED is not
  // functioning as expected for the screenshare toggling
  const onEndScreenshare = ({ attributes }) => {
    const screenshareUserId = Object.keys(remoteUserProps).find((jitsiUserId) =>
      /ScreenshareUser/.test(remoteUserProps[jitsiUserId].name),
    );

    if (screenshareUserId) {
      if (attributes.isEnded === 'true') {
        remoteUserProps[screenshareUserId].tracks[0].detach(videoAudioRefs[screenshareUserId][1]);
        remoteUserProps[screenshareUserId].screenshareOff = true;
      } else {
        remoteUserProps[screenshareUserId].screenshareOff = false;
        if (remoteUserProps[screenshareUserId].tracks[0]) {
          remoteUserProps[screenshareUserId].tracks[0].attach(videoAudioRefs[screenshareUserId][1]);
        }
      }
    }
  };

  /**
   * MuteAllParticipants Listener
   * Triggered by the Clinician. Mutes all participants audio
   */
  const onMuteAllParticipants = () => {
    if (!sessionStore.isClinician) {
      resetMicState(false);
    }
  };

  const onMuteSingleParticipant = ({ attributes }) => {
    // Find the participant
    if (attributes.participantJitsiId === myJitsiId.value) {
      resetMicState(false);
    }
  };

  const onMuteMedia = async ({ attributes }) => {
    const muted = attributes.muted.split(',');
    for (const mutedId of muted) {
      if (!remoteUserProps[mutedId]) {
        remoteUserProps[mutedId] = {};
      }
    }

    Object.keys(remoteUserProps).forEach((jitsiId) => {
      remoteUserProps[jitsiId].streamMuted = muted.includes(jitsiId);
    });
  };

  const onKickParticipant = ({ attributes }) => {
    if (attributes.participantJitsiId === myJitsiId.value) {
      // Take the user to the dashboard
      window.location.replace(route('dashboard'));
    }
  };

  const onRaiseHand = ({ attributes }) => {
    toggleHand(attributes.state, attributes.userId, attributes.time);
  };

  const commands = {
    [COMMANDS.RAISE_HAND]: onRaiseHand,
    [COMMANDS.MUTE_ALL_PARTICIPANTS]: onMuteAllParticipants,
    [COMMANDS.END_SCREENSHARE]: onEndScreenshare,
    [COMMANDS.MUTE_SINGLE_PARTICIPANT]: onMuteSingleParticipant,
    [COMMANDS.MUTE_MEDIA]: onMuteMedia,
    [COMMANDS.KICK_PARTICIPANT]: onKickParticipant,
  };

  return {
    events,
    commands,
  };
}

export default useConsultationHandlers;
